import React, { useState, useEffect } from 'react';
import { Loader, X, AlertCircle, ChevronLeft } from 'lucide-react';
import { CardEditor } from './CardEditor';
import { supabase } from '../lib/supabase';

interface CardSelectorProps {
  occasion: string;
  contact: {
    id: string;
    name: string;
    relationship: string;
    card_preference: string;
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
    gender: 'male' | 'female' | null;
  };
  onSelect: (cardId: string, message: string) => void;
  onClose: () => void;
}

export function CardSelector({ occasion, contact, onSelect, onClose }: CardSelectorProps) {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [cardImages, setCardImages] = useState<string[]>([]);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState<string>('');
  const initRef = React.useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    console.log('Starting initialization sequence');
    
    const initializeCard = async () => {
      try {
        await fetchUserProfile();
        await generateInitialMessage();
        await generateAllCardImages();
      } catch (error) {
        console.error('Initialization error:', error);
        setError('Failed to initialize card creation. Please try again.');
      }
    };

    initRef.current = true;
    initializeCard();
  }, []);

  async function generateInitialMessage() {
    setIsLoadingMessage(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-message', {
        body: {
          occasion,
          recipient: contact.name,
          relationship: contact.relationship,
          gender: contact.gender,
          tone: 'warm and sincere',
          guidelines: {
            structure: 'natural and flowing',
            length: 'moderate',
            style: 'personal and meaningful',
            focus: 'relationship and celebration',
            avoid: 'clichés and generic phrases'
          }
        }
      });
      
      if (error) throw error;
      setMessage(data.message);
    } catch (error) {
      console.error('Error generating message:', error);
      setError('Failed to generate message. Please try again or write your own message.');
    } finally {
      setIsLoadingMessage(false);
    }
  }

  async function generateAllCardImages() {
    if (isLoadingImages) return;
    
    console.log('Starting card image generation');
    setIsLoadingImages(true);
    setCardImages([]);
    setLoadingProgress('Generating card designs...');
    
    try {
      const genderStyle = contact.gender === 'male' 
        ? 'masculine and bold with strong geometric elements, darker color palette, architectural or structured designs'
        : 'feminine and elegant with flowing elements, soft color palette, organic or natural designs';

      const prompt = `Create a sophisticated greeting card design for a ${occasion} celebration. Requirements:
- Style: ${contact.card_preference}, ${genderStyle}
- Theme: Design suitable for a ${contact.relationship.toLowerCase()}
- Design: Professional and refined with NO text overlay
- Colors: ${contact.gender === 'male' ? 'Rich, deep colors with bold contrasts' : 'Soft, harmonious palette with gentle gradients'}
- Elements: ${contact.gender === 'male' ? 'Strong geometric shapes, architectural elements, or modern abstract patterns' : 'Flowing organic shapes, natural elements, or delicate patterns'}
- Mood: ${contact.card_preference === 'Funny' ? 'Playful and humorous' : 'Dignified and meaningful'}
The design should be visually striking while maintaining space for text.`;

      const { data, error } = await supabase.functions.invoke('generate-card-image', {
        body: {
          prompt,
          num_outputs: 3,
          guidance_scale: 8.5,
          num_inference_steps: 50,
          negative_prompt: contact.gender === 'male'
            ? "feminine elements, floral patterns, pastel colors, soft textures, delicate designs, cute, kawaii, childish"
            : "harsh geometric patterns, dark industrial elements, aggressive textures, overly masculine themes"
        }
      });

      if (error) throw error;
      if (Array.isArray(data?.imageUrls)) {
        setCardImages(data.imageUrls);
      } else {
        throw new Error('Invalid response format from image generation');
      }
    } catch (error) {
      console.error('Error generating card images:', error);
      setError('Failed to generate card designs. Please try again.');
    } finally {
      setIsLoadingImages(false);
      setLoadingProgress('');
    }
  }

  async function fetchUserProfile() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      setUserId(session.user.id);

      const { data: profile, error } = await supabase
        .from('users')
        .select('signature_url')
        .eq('user_id', session.user.id)
        .single();

      if (error) throw error;
      setSignatureUrl(profile?.signature_url || null);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }

  if (showEditor && selectedCard) {
    return (
      <CardEditor
        cardImage={cardImages[parseInt(selectedCard.split('-')[1])]}
        message={message}
        recipientName={contact.name}
        occasion={occasion}
        signatureUrl={signatureUrl}
        recipientAddress={contact}
        onApprove={(data) => {
          if (!data.to?.id) {
            onSelect(data.front, message);
          }
          onClose();
        }}
        onRequestChanges={() => setShowEditor(false)}
        onClose={() => setShowEditor(false)}
        userId={userId}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl flex flex-col h-[90vh]">
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold font-display text-brand-secondary">
            Select a Design for {contact.name}'s {occasion} Card
          </h2>
          <button 
            onClick={onClose}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message - if present */}
        {error && (
          <div className="flex-shrink-0 p-4 bg-red-50 border-b border-red-200 flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingImages || isLoadingMessage ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-12 h-12 text-brand-primary animate-spin mb-4" />
              <p className="text-brand-secondary text-lg mb-2">
                {loadingProgress || 'Preparing to generate card designs...'}
              </p>
              <div className="mt-4 max-w-md text-center text-sm text-brand-secondary opacity-75">
                We'll create some beautiful {contact.card_preference.toLowerCase()} designs 
                for your {contact.relationship.toLowerCase()}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 max-w-[600px] mx-auto">
              {cardImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                    selectedCard === `card-${index}` 
                      ? 'border-brand-primary shadow-lg scale-[1.02]' 
                      : 'border-transparent hover:border-brand-accent hover:scale-[1.01]'
                  }`}
                  onClick={() => setSelectedCard(`card-${index}`)}
                >
                  <img
                    src={imageUrl}
                    alt={`Card design ${index + 1}`}
                    className="w-full aspect-[4/3] object-cover"
                    loading="lazy"
                  />
                  {selectedCard === `card-${index}` && (
                    <div className="absolute inset-0 bg-brand-primary bg-opacity-10 flex items-center justify-center">
                      <div className="bg-brand-primary text-white px-3 py-1.5 rounded-full text-sm">
                        Selected
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fixed Footer with Navigation */}
        <div className="flex-shrink-0 border-t bg-white p-4">
          <div className="flex justify-between items-center max-w-4xl mx-auto w-full">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={() => selectedCard && setShowEditor(true)}
              disabled={!selectedCard || isLoadingMessage}
              className={`px-6 py-2 rounded-lg transition-all ${
                selectedCard && !isLoadingMessage
                  ? 'bg-brand-primary text-white hover:bg-brand-secondary'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoadingMessage ? 'Preparing Message...' : 'Customize Card'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}